import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { api } from "../lib/api";
import PluginCard from "../components/PluginCard";
import Logo from "../components/Logo";
import { useAuth } from "../lib/auth";
import Seo from "../components/Seo";
import { FadeUp, StaggerList, StaggerItem } from "../components/Motion";
import AnimatedCounter from "../components/AnimatedCounter";
import {
  ArrowRight,
  Apple,
  Bot,
  Boxes,
  Cpu,
  Download,
  Eye,
  Github,
  KeyRound,
  Lock,
  Mic,
  MonitorDown,
  Plug,
  ShieldCheck,
  Sparkles,
  Star,
  Store,
  Terminal,
  Workflow,
} from "lucide-react";

function formatSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
}

export default function Home() {
  const { user } = useAuth();
  // Sprint 2.16 — null = still loading, [] = loaded but empty.
  // Used so MarketplaceTeaser can paint skeleton placeholders during
  // fetch instead of the "Featured catalog is waiting…" empty card,
  // which used to flash on every fresh page load.
  const [featured, setFeatured] = useState(null);
  const [downloads, setDownloads] = useState(null);

  useEffect(() => {
    api.get("/api/plugins?featured=true&limit=6")
      .then((r) => setFeatured(r.data))
      .catch(() => setFeatured([]));
    api.get("/api/download/status").then((r) => setDownloads(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="overflow-hidden">
      <Seo
        title="The personal AI agent that runs on your machine"
        description="v0.0.3 · Desktop · Terminal CLI · Mobile PWA · plugin marketplace. Bring your own keys, your data stays local. 25 AI providers, 50+ commands, 6 sandbox backends, 30+ skills, 13-layer memory."
        keywords="AI agent, desktop AI, BYOK, local-first, Claude, GPT, Gemini, Ollama"
      />
      <ScrollProgress />
      <Hero user={user} />
      <ProviderStrip />
      <FadeUp><TrustStrip /></FadeUp>
      <FadeUp delay={0.05}><Features /></FadeUp>
      <FadeUp delay={0.1}><HowItWorks /></FadeUp>
      <FadeUp delay={0.05}><DownloadSection downloads={downloads} /></FadeUp>
      <FadeUp delay={0.05}><MarketplaceTeaser plugins={featured} /></FadeUp>
      <FadeUp delay={0.05}><WorkflowsCallout /></FadeUp>
      <FadeUp delay={0.05}><CreatorsCTA /></FadeUp>
      <FadeUp delay={0.05}><FinalCTA /></FadeUp>
    </div>
  );
}

// Sprint 8 polish — global page-scroll progress bar at the very top of
// the viewport. Reads window.scrollY against document height and writes
// the resulting % into the .scroll-progress element's width. Pure RAF so
// it never blocks paint. Hidden on prefers-reduced-motion via CSS.
function ScrollProgress() {
  const ref = useRef(null);
  useEffect(() => {
    let raf = 0;
    const update = () => {
      const el = ref.current;
      if (!el) return;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const pct = max > 0 ? (window.scrollY / max) * 100 : 0;
      el.style.width = `${Math.min(100, Math.max(0, pct))}%`;
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);
  return <div ref={ref} className="scroll-progress" style={{ width: "0%" }} aria-hidden="true" />;
}

function Hero({ user }) {
  // Phase 20.2 — scroll-reveal storytelling. As the user scrolls past
  // the hero, the mesh blobs parallax up, the backdrop fades, the
  // status metric row gently lifts + dims, and the brand card eases
  // out. The headline itself stays anchored — we don't want to hide
  // the value prop on scroll, just let the supporting visuals breathe.
  const reduce = useReducedMotion();
  const heroRef = useRef(null);
  // Sprint 8 polish — fetch live GitHub stars + show on the hero pill.
  // Cached against ratelimit failures (the API returns 60/hr without auth).
  const [stars, setStars] = useState(null);
  useEffect(() => {
    fetch("https://api.github.com/repos/ErnestKostevich/horizon-genesis", {
      headers: { Accept: "application/vnd.github+json" },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (j) setStars(j.stargazers_count ?? null); })
      .catch(() => {});
  }, []);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  // When prefers-reduced-motion is on, all transforms collapse to a
  // no-op so the page stays static.
  const meshY    = useTransform(scrollYProgress, [0, 1], reduce ? ["0%", "0%"] : ["0%", "-30%"]);
  const meshOp   = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1, 0.35]);
  const backOp   = useTransform(scrollYProgress, [0, 0.8], reduce ? [1, 1] : [1, 0.4]);
  const cardY    = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [0, -40]);
  const cardOp   = useTransform(scrollYProgress, [0, 0.9], reduce ? [1, 1] : [1, 0.45]);
  const statsY   = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [0, 30]);
  const statsOp  = useTransform(scrollYProgress, [0, 0.85], reduce ? [1, 1] : [1, 0.4]);
  const titleY   = useTransform(scrollYProgress, [0, 1], reduce ? [0, 0] : [0, -20]);

  // Sprint 8 polish — cursor-tracking halo. Updates --hero-mx / --hero-my
  // CSS variables on the hero element so the .hero-halo radial gradient
  // follows the pointer. RAF-throttled, disabled for reduced-motion, and
  // skipped on touch (pointerType === "touch").
  useEffect(() => {
    if (reduce) return;
    const el = heroRef.current;
    if (!el) return;
    let raf = 0;
    const onMove = (e) => {
      if (e.pointerType === "touch") return;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        el.style.setProperty("--hero-mx", `${x}%`);
        el.style.setProperty("--hero-my", `${y}%`);
      });
    };
    el.addEventListener("pointermove", onMove);
    return () => {
      el.removeEventListener("pointermove", onMove);
      cancelAnimationFrame(raf);
    };
  }, [reduce]);

  return (
    <section
      ref={heroRef}
      className="relative border-b border-white/5 overflow-hidden bg-bg animate-fade-in hero-section"
      // Phase 20.4 — Hero adapts to active theme. Dark theme keeps the
      // cinematic mesh + dark backdrop. Light theme renders a clean
      // white surface with the same animated mesh but softer hues and
      // dark text. CSS scope `[data-theme="light"] .hero-section` does
      // the heavy lifting in index.css.
    >
      {/* Phase 19 — animated mesh background + grid overlay */}
      <motion.div style={{ y: meshY, opacity: meshOp }} className="absolute inset-0 will-change-transform">
        <HeroMesh />
      </motion.div>
      <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
      <motion.div style={{ opacity: backOp }} className="absolute inset-0">
        <HeroBackdrop />
      </motion.div>
      {/* Sprint 8 polish — cursor-tracking halo. Sits above backdrop but
          below the content so the highlight doesn't dim the text. */}
      <div className="hero-halo" aria-hidden="true" />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-14 sm:py-16 lg:py-20 xl:py-24 relative z-10 w-full">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_420px] xl:grid-cols-[minmax(0,1fr)_480px] gap-10 lg:gap-14 items-center">
          <motion.div style={{ y: titleY }} className="min-w-0 animate-fade-in-up">
            {/* Eyebrow with live status pulse */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex items-center gap-3 mb-5 sm:mb-6"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping-slow absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400"></span>
              </span>
              <div className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.34em] text-accent">
                horizon genesis · local-first ai layer
              </div>
            </motion.div>

            {/* Gradient headline — each word fades up with a small stagger.
                Creates a "typewriter without the click" feel on first
                paint. */}
            {/* H1 — kept short and concrete. The differentiator is "your
                machine", not "premium". "personal AI agent" is the category
                we own; "your machine" is the moat. Gradient highlight on
                the last 2 words gives the visual hook without splitting
                the sentence with marketing fluff. */}
            <motion.h1
              initial={reduce ? false : "hidden"}
              animate={reduce ? false : "show"}
              variants={{
                hidden: { },
                show: { transition: { staggerChildren: 0.08, delayChildren: 0.15 } },
              }}
              className="font-display text-[42px] sm:text-5xl md:text-6xl xl:text-[78px] leading-[1.05] tracking-tight text-white max-w-[980px] drop-shadow-2xl"
            >
              {[
                "The",
                "personal",
                "AI",
                "agent",
                "that",
                "runs",
                "on",
              ].map((w, i) => (
                <motion.span
                  key={`a-${i}`}
                  variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.21, 0.47, 0.32, 0.98] } } }}
                  className="inline-block mr-[0.22em]"
                >
                  {w}
                </motion.span>
              ))}
              <motion.span
                variants={{ hidden: { opacity: 0, y: 14 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] } } }}
                className="inline-block bg-clip-text text-transparent bg-gradient-to-r from-brand-cyan via-brand-violet to-brand-amber bg-[length:200%_auto] animate-gradient-shift mr-[0.22em]"
              >
                your machine.
              </motion.span>
            </motion.h1>

            {/* Subtitle reinforces ONE idea: local-first + BYOK + learns
                you. Not a feature list. Resist the urge to add commas. */}
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-5 sm:mt-6 font-body text-base sm:text-lg text-text2 max-w-3xl leading-relaxed"
            >
              Local-first. Bring your own model — Claude, GPT, Gemini, or fully
              offline with Ollama. The agent learns who you are and stays on your
              hardware, not in someone else's cloud.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.85 }}
              className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-wrap"
            >
              <a
                href="#download"
                className="group relative font-display text-sm font-semibold px-7 h-12 flex items-center justify-center text-bg rounded-full overflow-hidden transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
                data-testid="hero-download"
              >
                {/* Multi-layer gradient background with shift on hover */}
                <span className="absolute inset-0 bg-gradient-to-r from-amber1 via-amber2 to-amber1 bg-[length:200%_auto] group-hover:bg-[position:100%_center] transition-[background-position] duration-500" />
                <span className="absolute inset-0 shadow-[0_0_25px_rgba(244,201,93,0.55)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full" />
                <span className="relative flex items-center">
                  <Download size={16} className="mr-2" /> Download Horizon
                </span>
              </a>
              <Link
                to="/about"
                className="group font-display text-sm font-medium px-6 h-12 flex items-center justify-center border border-accent/50 text-accent hover:bg-accent hover:text-bg shadow-[0_0_15px_rgba(56,189,248,0.1)] hover:shadow-glow-cyan rounded-full transition-all duration-300"
                data-testid="hero-about"
              >
                Product architecture
                <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/browse"
                className="font-display text-sm font-medium px-6 h-12 flex items-center justify-center border border-white/10 hover:border-white/30 text-text1 rounded-full bg-white/5 hover:bg-white/10 transition-all duration-300"
                data-testid="hero-browse"
              >
                <Store size={16} className="mr-2" /> Marketplace
              </Link>
              {/* Sprint 8 polish — live GitHub stars badge. Communicates real
                  traction without me having to bake the number into copy. */}
              <a
                href="https://github.com/ErnestKostevich/horizon-genesis"
                target="_blank"
                rel="noopener noreferrer"
                className="group font-mono text-xs uppercase tracking-wider px-4 h-12 inline-flex items-center gap-2.5 border border-white/10 hover:border-amber1/50 text-text1 rounded-full bg-white/5 hover:bg-white/10 transition-all duration-300"
                data-testid="hero-stars"
                aria-label="Star Horizon on GitHub"
              >
                <Github size={14} className="text-text2 group-hover:text-text1 transition-colors" />
                <span className="text-text2 group-hover:text-text1 transition-colors">stars</span>
                <span className="inline-flex items-center gap-1 text-amber1 font-semibold border-l border-white/10 pl-2.5">
                  <Star size={12} fill="currentColor" strokeWidth={0} />
                  {stars !== null ? <AnimatedCounter value={stars} /> : "—"}
                </span>
              </a>
              {user?.is_admin && (
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/download/horizon-complete.zip`}
                  className="font-mono text-[11px] uppercase px-4 h-11 flex items-center justify-center border border-border1 text-text2 hover:border-amber1 hover:text-amber1 rounded-md"
                  data-testid="hero-download-bundle"
                  onClick={async (e) => {
                    e.preventDefault();
                    const r = await fetch(e.currentTarget.href, {
                      headers: { Authorization: `Bearer ${localStorage.getItem("hrzn_token")}` },
                    });
                    if (!r.ok) { alert("Admin auth required"); return; }
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "horizon-complete.zip";
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  source bundle
                </a>
              )}
            </motion.div>
          </motion.div>
          <motion.div style={{ y: cardY, opacity: cardOp }} className="lg:justify-self-end w-full">
            {/* Phase 27 — replaced the self-branding card with a mock
                terminal session. Shows the actual experience of running
                Horizon — first-run banner, prompt, tool calls, and a
                "goal met" outcome — instead of restating that we exist.
                TODO: when a real demo recording is available, swap this
                for a <video autoplay loop muted playsInline src="/demo.mp4">
                (target 15s, 1280x800). Static mock is safer for now. */}
            <HeroTerminal />
          </motion.div>
        </div>

        <motion.div
          style={{ y: statsY, opacity: statsOp }}
          className="mt-10 sm:mt-12 grid grid-cols-2 md:grid-cols-4 border border-white/10 bg-surface/50 backdrop-blur-xl rounded-2xl overflow-hidden max-w-5xl shadow-2xl shadow-black/50 animate-fade-in-up will-change-transform"
        >
          <StatusMetric label="Local Models" status="Active" color="bg-emerald-400" body="Run LLMs on your hardware" />
          <StatusMetric label="BYOK Connect" status="Online" color="bg-accent" borderLeft body="Gemini, Groq, Claude, local, OpenAI-compatible" />
          <StatusMetric label="Marketplace" status="Moderated" color="bg-amber1" borderLeft body="Safe, reviewed extensions" />
          <StatusMetric label="Core License" status="BSL 1.1" color="bg-white" borderLeft body="Source-visible for audit" />
        </motion.div>
      </div>
    </section>
  );
}

function HeroBackdrop() {
  // Phase 20.4 — theme-aware backdrop. The inline gradients used to be
  // hardcoded to dark navy, which made the Hero stay black even when
  // light theme was active. CSS classes now drive the visuals so the
  // [data-theme="light"] .hero-section override in index.css can flip
  // them to a clean white variant.
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <div className="absolute inset-0 hero-backdrop-layer" />
      <div className="hidden lg:block absolute right-[4%] top-[16%] w-[34vw] max-w-[560px] h-[62%] rounded-[32px] hero-backdrop-glow blur-2xl opacity-70" />
      <div className="hidden lg:block absolute right-0 top-0 h-full w-[44%] hero-backdrop-grid" style={{
        background:
          "linear-gradient(90deg, transparent, rgba(13,20,32,.46)), repeating-linear-gradient(0deg, transparent 0, transparent 56px, rgba(154,168,186,.028) 57px), repeating-linear-gradient(90deg, transparent 0, transparent 56px, rgba(154,168,186,.022) 57px)",
      }} />
    </div>
  );
}

// Phase 19 — Animated mesh background. Three drifting blob shapes
// behind the hero text + tiny floating particle dots. Pure CSS so
// it costs ~nothing.
function HeroMesh() {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {/* Three colour blobs that drift slowly */}
      <div className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full opacity-50 blur-3xl bg-brand-cyan/20 animate-blob" style={{ animationDuration: "16s" }} />
      <div className="absolute top-1/3 left-1/2 w-[600px] h-[600px] rounded-full opacity-40 blur-3xl bg-brand-violet/22 animate-blob" style={{ animationDuration: "20s", animationDelay: "-4s" }} />
      <div className="absolute -bottom-40 right-0 w-[560px] h-[560px] rounded-full opacity-45 blur-3xl bg-brand-amber/15 animate-blob" style={{ animationDuration: "18s", animationDelay: "-8s" }} />
      {/* Floating particle dots — pure CSS via box-shadow tricks */}
      <div className="absolute top-1/4 left-1/3 w-1 h-1 rounded-full bg-brand-cyan/50 animate-float" />
      <div className="absolute top-3/4 left-1/4 w-1.5 h-1.5 rounded-full bg-brand-violet/40 animate-float-slow" style={{ animationDelay: "-2s" }} />
      <div className="absolute top-1/2 right-1/3 w-1 h-1 rounded-full bg-brand-amber/55 animate-float" style={{ animationDelay: "-1s" }} />
      <div className="absolute top-1/3 right-1/4 w-2 h-2 rounded-full bg-brand-pink/30 animate-float-slow" style={{ animationDelay: "-3s" }} />
      <div className="absolute bottom-1/4 right-1/2 w-1 h-1 rounded-full bg-emerald-400/40 animate-float" style={{ animationDelay: "-4s" }} />
    </div>
  );
}

function HeroBrandCard() {
  return (
    <div className="hidden lg:block bg-surface/60 border border-white/10 backdrop-blur-2xl rounded-2xl p-6 xl:p-8 w-full max-w-[460px] justify-self-end shadow-[0_0_50px_rgba(0,0,0,0.5)] animate-fade-in-up transition-transform duration-500 hover:scale-[1.02]">
      <div className="flex items-center gap-5 pb-5 border-b border-white/5">
        <Logo size={76} />
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-amber1">Horizon Genesis</div>
          <div className="font-display text-2xl font-semibold text-white mt-1">Desktop AI workspace</div>
        </div>
      </div>
      <p className="font-body text-sm text-text2 leading-relaxed mt-5">
        A private AI operating layer for local models, BYOK providers, moderated plugins, and developer workflows.
      </p>
      <div className="grid grid-cols-2 gap-3 mt-5">
        {[
          ["Local-first", "Runs on your machine"],
          ["BYOK", "Your provider keys"],
          ["Plugins", "Moderated catalog"],
          ["BSL 1.1", "Source-visible core"],
        ].map(([title, body]) => (
          <div key={title} className="rounded-lg border border-border1 bg-bg/45 p-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber1">{title}</div>
            <div className="font-body text-xs text-text2 mt-2 leading-relaxed">{body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// HeroTerminal — mock CLI session shown beside the H1.
// This is the "asciinema placeholder" — communicates the product
// without needing a real recording yet. Colors match the CLI banner:
//   #7c6df2 violet for accents, dim grey for hints, emerald for ✓.
// Replace with <video src="/demo.mp4"> when a real recording exists.
//
// Phase 27.1 — theme-aware. The terminal used to be a hard-coded
// #0b0f1a slab which looked like a stuck broken element on the
// white light-theme page. Token classes (`hero-terminal*` + `htok-*`)
// are defined in index.css and flip via `[data-theme="light"]` —
// same pattern as `.install-terminal` on /download.
function HeroTerminal() {
  return (
    <div className="hidden lg:block w-full max-w-[520px] justify-self-end animate-fade-in-up">
      <div className="rounded-xl border hero-terminal overflow-hidden transition-transform duration-500 hover:scale-[1.01]">
        {/* Title bar */}
        <div className="hero-terminal-bar flex items-center gap-2 px-4 py-2.5 border-b">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/80" />
          </div>
          <span className="hero-terminal-label ml-2 font-mono text-[10.5px] uppercase tracking-[0.22em]">
            ~/projects · horizon
          </span>
        </div>
        {/* Body — styled to match the CLI's gradient banner palette */}
        <pre className="hero-terminal-body m-0 p-5 sm:p-6 font-mono text-[12px] leading-[1.65] overflow-x-auto whitespace-pre">
<span className="htok-accent font-semibold">{"≁ horizon"}</span>  <span className="htok-dim">v0.0.3</span>  <span className="htok-faint">·</span>  <span className="htok-dim">bring your own model</span>{"\n\n"}<span className="htok-dim">provider</span> <span className="htok-strong">gemini</span>   <span className="htok-dim">persona</span> <span className="htok-strong">jarvis</span>   <span className="htok-dim">memory</span> <span className="htok-strong">13 layers</span>   <span className="htok-dim">skills</span> <span className="htok-strong">30+</span>{"\n\n"}<span className="htok-faint">{"▌ Type a message and press Enter to send."}</span>{"\n\n"}<span className="htok-accent">{"›"}</span> <span className="htok-strong">build a landing page that explains my product</span>{"\n"}  <span className="htok-ok">{"✓"}</span> <span className="htok-tool">web_search</span> <span className="htok-faint">·</span> <span className="htok-dim">"landing page best practices 2026"</span>  <span className="htok-faint">(1.2s)</span>{"\n"}  <span className="htok-ok">{"✓"}</span> <span className="htok-tool">write_file</span> <span className="htok-faint">·</span> <span className="htok-dim">~/landing/index.html</span>  <span className="htok-faint">(0.4s)</span>{"\n"}  <span className="htok-ok">{"✓"}</span> <span className="htok-tool">run_shell</span> <span className="htok-faint">·</span> <span className="htok-dim">"npx serve ."</span>  <span className="htok-faint">(12.1s)</span>{"\n"}  <span className="htok-accent">{"●"}</span> <span className="htok-tool">goal met</span>  <span className="htok-faint">confidence=high</span>{"\n\n"}<span className="htok-strong">Done</span> <span className="htok-faint">— preview running at</span> <span className="htok-accent">http://localhost:3000</span>
        </pre>
      </div>
    </div>
  );
}

function StatusMetric({ label, status, color, borderLeft, body }) {
  return (
    <div className={`p-4 sm:p-5 flex flex-col justify-center ${borderLeft ? "md:border-l border-white/5" : ""} border-b md:border-b-0 border-white/5 bg-white/[0.01] hover:bg-white/[0.04] transition-colors`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-text2 mb-3">{label}</div>
      <div className="flex items-center gap-2.5">
        <div className="relative flex h-2.5 w-2.5">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${color}`}></span>
          <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${color}`}></span>
        </div>
        <span className="font-display text-sm font-medium text-white tracking-wide">{status}</span>
      </div>
      <div className="font-body text-[11px] text-text2 mt-2 leading-relaxed opacity-70">{body}</div>
    </div>
  );
}

// Sprint 8 polish — provider/runtime wordmark marquee. Builds trust by
// showing that Horizon is BYOK across every major AI provider. Two
// identical lists are rendered side-by-side and the .marquee-track CSS
// shifts -50% so the loop is seamless. Paused on hover.
const PROVIDERS = [
  "Anthropic",
  "OpenAI",
  "Google Gemini",
  "Mistral",
  "Groq",
  "Cohere",
  "Ollama",
  "LM Studio",
  "OpenRouter",
  "DeepSeek",
  "xAI Grok",
  "Together",
];

function ProviderStrip() {
  return (
    <section className="relative border-b border-border1 bg-surface/30 backdrop-blur-sm overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-7 sm:py-9">
        <div className="flex items-center justify-between gap-6 mb-5 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.34em] text-accent">/byok · 25+ providers</div>
            <div className="font-display text-base sm:text-lg text-text1 mt-1.5 font-medium">
              Bring any model — cloud, local, or routed
            </div>
          </div>
          <div className="font-mono text-[11px] text-text2 inline-flex items-center gap-2">
            <span className="live-dot" /> all wired in v0.0.3
          </div>
        </div>
        <div className="marquee-mask">
          <div className="marquee-track">
            {[...PROVIDERS, ...PROVIDERS].map((name, i) => (
              <span
                key={`${name}-${i}`}
                className="font-display font-semibold text-base sm:text-lg text-text2/80 hover:text-text1 tracking-tight whitespace-nowrap transition-colors duration-300 select-none"
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TrustStrip() {
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 sm:py-10 border-b border-border1">
      {/* Phase 19 — Animated counter stats row above the trust items.
          Counts up when scrolled into view. Each stat has a gradient
          text + an accent colour to match the brand palette. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border1 border border-border1 mb-10 rounded-2xl overflow-hidden">
        <StatCounter value={25}  suffix="+" label="AI providers"     accent="from-brand-cyan to-brand-violet" />
        <StatCounter value={30}  suffix="+" label="Built-in skills"  accent="from-brand-violet to-brand-pink" />
        <StatCounter value={50}  suffix="+" label="CLI commands"     accent="from-brand-pink to-brand-amber" />
        <StatCounter value={9}              label="Memory layers"    accent="from-brand-amber to-brand-cyan" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-border1">
        <TrustItem icon={Lock} title="Local by default" body="Provider keys and local workflows stay on the device. Horizon servers handle account, license, catalog, install, and publish state." />
        <TrustItem icon={Eye} title="Permissions visible" body="Every plugin declares the resources it touches before install. Community execution stays disabled until sandbox review ships." borderLeft />
        <TrustItem icon={ShieldCheck} title="Source-visible" body="Core code is readable for audit and learning under BSL 1.1. Commercial use needs permission." borderLeft />
      </div>
    </section>
  );
}

function StatCounter({ value, suffix = "", label, accent }) {
  return (
    <div className="bg-surface px-4 sm:px-6 py-5 sm:py-6 text-center group hover:bg-surface2 transition-colors">
      <div className={`font-display text-3xl sm:text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${accent}`}>
        <AnimatedCounter value={value} suffix={suffix} />
      </div>
      <div className="mt-1.5 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-text2 group-hover:text-text1 transition-colors">
        {label}
      </div>
    </div>
  );
}

function TrustItem({ icon: Icon, title, body, borderLeft }) {
  return (
    <div className={`p-6 bg-surface border-t md:border-t-0 ${borderLeft ? "md:border-l" : ""} border-border1`}>
      <Icon size={20} strokeWidth={1.5} className="text-amber1 mb-3" />
      <div className="font-display text-sm uppercase text-text1">{title}</div>
      <p className="font-body text-sm text-text2 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function Features() {
  return (
    <section id="features" className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-border1">
      <SectionHeader eyebrow="/product" title="Not another empty AI shell" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
        <Feature icon={Bot}      tint="cyan"   title="Desktop agent"       body="A real local app with chat, voice, screen context, workflows, provider switching, and Pro license state." />
        <Feature icon={KeyRound} tint="violet" title="BYOK & local models" body="OpenAI, Anthropic, Gemini, Groq, Ollama, LM Studio, and local OpenAI-compatible endpoints are first-class settings." />
        <Feature icon={Plug}     tint="pink"   title="Plugins"              body="Plugins install through the marketplace flow. Community execution is held behind the sandbox gate instead of being marketed as safe too early." />
        <Feature icon={Workflow} tint="amber"  title="Workflows"            body="Recipe-style automations for repeated work, with publish and moderation tied to the same account model." />
        <Feature icon={Cpu}      tint="cyan"   title="Computer use"         body="Mouse, keyboard, screenshot, smart_click, OCR, multi-display, and recorded macros — all permission-gated and routed through the desktop main process." />
        <Feature icon={Boxes}    tint="violet" title="Developer surface"    body="Plugin SDK, docs, moderation, creator profile, wallet, payout, and dashboard states are part of the release surface." />
      </div>
    </section>
  );
}

const TINTS = {
  cyan:   { border: "hover:border-brand-cyan/40",   glow: "group-hover:shadow-glow-cyan",   icon: "text-brand-cyan",   bg: "from-brand-cyan/8 via-transparent to-transparent" },
  violet: { border: "hover:border-brand-violet/40", glow: "group-hover:shadow-glow-violet", icon: "text-brand-violet", bg: "from-brand-violet/8 via-transparent to-transparent" },
  pink:   { border: "hover:border-brand-pink/40",   glow: "group-hover:shadow-glow-pink",   icon: "text-brand-pink",   bg: "from-brand-pink/8 via-transparent to-transparent" },
  amber:  { border: "hover:border-brand-amber/40",  glow: "group-hover:shadow-glow-amber",  icon: "text-brand-amber",  bg: "from-brand-amber/8 via-transparent to-transparent" },
};

function Feature({ icon: Icon, title, body, tint = "cyan" }) {
  const t = TINTS[tint] || TINTS.cyan;
  return (
    <div className={`group relative p-6 rounded-2xl border border-border1 bg-surface ${t.border} hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-default`}>
      {/* Hover-only gradient overlay */}
      <div className={`absolute inset-0 bg-gradient-to-br ${t.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />
      {/* Glow shadow that fades in */}
      <div className={`absolute inset-0 rounded-2xl ${t.glow} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />

      <div className="relative">
        <div className={`inline-flex w-10 h-10 items-center justify-center rounded-xl bg-white/5 ${t.icon} mb-4 group-hover:scale-110 group-hover:bg-white/10 transition-transform duration-300`}>
          <Icon size={20} strokeWidth={1.6} />
        </div>
        <div className="font-display text-base font-semibold text-text1 mb-1.5">{title}</div>
        <p className="font-body text-sm text-text2 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function HowItWorks() {
  return (
    <section id="how" className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-border1">
      <SectionHeader eyebrow="/flow" title="From install to controlled automation" />
      <div className="relative grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
        {/* Phase 20 — connecting line (desktop only). Sits behind the cards
            and runs through the icon row, like a circuit trace between
            the three brand-tinted steps. */}
        <div className="hidden md:block absolute top-[72px] left-[18%] right-[18%] h-px bg-gradient-to-r from-brand-cyan/40 via-brand-violet/40 to-brand-amber/40 pointer-events-none z-0" />
        <Step n="01" tint="cyan"   title="Install and sign in"  body="The app shows the actual marketplace account and clears stale tokens on auth failures." icon={Download} />
        <Step n="02" tint="violet" title="Connect a model"      body="Use cloud keys or local endpoints. Users see what is local, what is paid, and what requires a provider key." icon={KeyRound} />
        <Step n="03" tint="amber"  title="Run with guardrails"  body="Marketplace installs, publish, wallet, payouts, moderation, and Pro status use backend state instead of fake local success." icon={Mic} />
      </div>
    </section>
  );
}

function Step({ n, title, body, icon: Icon, tint = "cyan" }) {
  const t = TINTS[tint] || TINTS.cyan;
  return (
    <div className={`group relative z-10 p-7 sm:p-8 rounded-2xl border border-border1 bg-surface ${t.border} hover:-translate-y-1 transition-all duration-300 overflow-hidden cursor-default`}>
      {/* Hover gradient + glow — same pattern as Feature card */}
      <div className={`absolute inset-0 bg-gradient-to-br ${t.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />
      <div className={`absolute inset-0 rounded-2xl ${t.glow} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />
      <div className="relative">
        <div className="flex items-start justify-between mb-5">
          <span className={`font-display text-[44px] font-bold ${t.icon} leading-none tracking-tight`}>{n}</span>
          <div className={`w-11 h-11 rounded-xl bg-white/5 ${t.icon} flex items-center justify-center group-hover:scale-110 group-hover:bg-white/10 transition-transform duration-300`}>
            <Icon size={22} strokeWidth={1.6} />
          </div>
        </div>
        <div className="font-display text-lg font-semibold text-text1 mb-1.5">{title}</div>
        <p className="font-body text-sm text-text2 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function DownloadSection({ downloads }) {
  return (
    <section id="download" className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-border1" data-testid="download-section">
      <div className="mb-6 sm:mb-8">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent">/download / v{downloads?.version || "0.0.3"}</div>
        <h2 className="font-display text-2xl md:text-3xl tracking-tight text-text1 mt-2">Get Horizon on your desktop</h2>
        <p className="font-body text-sm text-text2 mt-2 max-w-xl leading-relaxed">
          Source-visible. Bring your own API keys or run local models. New accounts get
          a 5-day free trial, then Pro billing unlocks continued agent access.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DownloadTile os="Windows" platform="windows" tint="cyan"   icon={MonitorDown} info={downloads?.windows} hint="Windows 10 / 11 · x64 · NSIS installer" />
        <DownloadTile os="macOS"   platform="mac"     tint="violet" icon={Apple}       info={downloads?.mac}     hint="macOS 11+ · Intel + Apple Silicon · .dmg" />
        <DownloadTile os="Linux"   platform="linux"   tint="amber"  icon={Terminal}    info={downloads?.linux}   hint="Ubuntu / Debian · x64 · AppImage · .deb" />
      </div>
      <div className="mt-6 font-mono text-[11px] text-text2 leading-relaxed">
        By downloading you agree to the BSL 1.1 license. Source is public — review before running.
      </div>
    </section>
  );
}

function DownloadTile({ os, platform, icon: Icon, info, hint, tint = "cyan" }) {
  const t = TINTS[tint] || TINTS.cyan;
  const available = info?.available;
  const href = info?.url || `${process.env.REACT_APP_BACKEND_URL}/api/download/${platform}`;
  const isExternal = /^https?:\/\//i.test(info?.url || "");
  return (
    <div className={`group relative p-6 sm:p-7 rounded-2xl border border-border1 bg-surface ${t.border} hover:-translate-y-1 transition-all duration-300 overflow-hidden`}>
      {/* Tinted hover overlay + glow — matches Feature/Step pattern */}
      <div className={`absolute inset-0 bg-gradient-to-br ${t.bg} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />
      <div className={`absolute inset-0 rounded-2xl ${t.glow} opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`} />

      <div className="relative">
        <div className="flex items-center gap-4 mb-5">
          <div className={`w-14 h-14 rounded-2xl bg-white/5 ${t.icon} flex items-center justify-center group-hover:scale-110 group-hover:bg-white/10 transition-transform duration-300 shrink-0`}>
            <Icon size={28} strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <div className="font-display text-xl font-semibold text-text1">{os}</div>
            <div className="font-mono text-[10px] text-text2 mt-1 uppercase tracking-[0.16em] leading-relaxed">{hint}</div>
          </div>
        </div>
        <div className="font-mono text-[11px] text-text2 mb-5 break-words bg-black/30 rounded-lg px-3 py-2.5 border border-white/5 leading-relaxed min-h-[44px]">
          {available ? (
            <>Build: <span className="text-text1">{info.filename}</span>{info.size ? <> · <span className="text-text1">{formatSize(info.size)}</span></> : null}</>
          ) : (
            <span className="text-text2/70">Installer not published to this instance yet.</span>
          )}
        </div>
        {available ? (
          <a
            href={href}
            className="font-mono text-xs uppercase px-5 h-11 inline-flex items-center justify-center bg-amber1 text-black hover:bg-amber2 rounded-lg w-full transition-colors"
            data-testid={`download-${platform}`}
            {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : { download: true })}
          >
            <Download size={14} className="mr-2" /> Download for {os}
          </a>
        ) : (
          <span className="font-mono text-xs uppercase px-5 h-11 inline-flex items-center justify-center border border-border1 text-text2/60 cursor-not-allowed rounded-lg w-full">
            Coming soon
          </span>
        )}
      </div>
    </div>
  );
}

function MarketplaceTeaser({ plugins }) {
  // Sprint 2.16 — three states:
  //   plugins === null  → still fetching, paint 6 skeleton cards
  //   plugins.length 0  → backend returned empty, show "waiting for seed"
  //   plugins.length > 0 → real cards
  const loading = plugins === null;
  const empty = !loading && plugins.length === 0;
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-border1">
      <div className="flex items-end justify-between mb-6 sm:mb-8 gap-6 flex-wrap">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-text2">/marketplace</div>
          <h2 className="font-display text-2xl md:text-3xl tracking-tight text-text1 mt-2">Marketplace is one part of the product</h2>
          <p className="font-body text-sm text-text2 mt-2 max-w-xl leading-relaxed">
            Plugins and workflows live inside the Horizon Genesis site. Every public
            listing is moderated, source-visible, and explicit about permissions.
          </p>
        </div>
        <Link to="/browse" className="font-mono text-xs uppercase px-5 h-11 flex items-center justify-center border border-amber1 text-amber1 hover:bg-amber1 hover:text-black" data-testid="marketplace-browse">
          Browse all <ArrowRight size={14} className="ml-2" />
        </Link>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 border-l border-b border-border1" data-testid="featured-skeleton">
          {Array.from({ length: 6 }).map((_, i) => (
            <FeaturedPluginSkeleton key={i} delay={i * 70} />
          ))}
        </div>
      ) : empty ? (
        <div className="font-mono text-xs text-text2 border border-border1 p-6">
          Featured catalog is waiting for backend seed/deploy.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 border-l border-b border-border1">
          {plugins.slice(0, 8).map((p) => <PluginCard key={p.id} plugin={p} />)}
        </div>
      )}
    </section>
  );
}

// Sprint 2.16 — skeleton card for featured plugins while data is in
// flight. Shape matches PluginCard so the layout doesn't reflow when
// real data lands. Staggered delay creates a soft wave instead of all
// six cards strobing in unison.
function FeaturedPluginSkeleton({ delay = 0 }) {
  return (
    <div className="border-r border-t border-border1 p-5 bg-surface/30">
      <div className="animate-pulse" style={{ animationDelay: `${delay}ms` }}>
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-surface2/60" />
          <div className="flex-1 min-w-0">
            <div className="h-4 w-3/4 bg-surface2/60 rounded mb-2" />
            <div className="h-3 w-1/2 bg-surface2/40 rounded" />
          </div>
        </div>
        <div className="h-3 w-full bg-surface2/40 rounded mb-1.5" />
        <div className="h-3 w-5/6 bg-surface2/40 rounded mb-1.5" />
        <div className="h-3 w-2/3 bg-surface2/40 rounded mb-5" />
        <div className="flex gap-2">
          <div className="h-5 w-14 bg-surface2/50 rounded-full" />
          <div className="h-5 w-16 bg-surface2/50 rounded-full" />
          <div className="h-5 w-12 bg-surface2/50 rounded-full" />
        </div>
      </div>
    </div>
  );
}

function WorkflowsCallout() {
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-border1">
      <div className="border border-border1 bg-surface p-6 sm:p-10 md:p-14 grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-8">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Workflow size={20} strokeWidth={1.5} className="text-amber1" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-text2">/workflows</span>
          </div>
          <h2 className="font-display text-2xl md:text-4xl text-text1 leading-tight">
            Workflows turn the agent into a controlled operating layer.
          </h2>
          <p className="mt-4 font-body text-sm md:text-base text-text2 leading-relaxed max-w-xl">
            Build a sequence of prompts, tool calls, and local actions. Publish
            templates only through the web dashboard so moderation, ownership,
            and payouts are recorded correctly.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/browse?type=workflow" className="font-mono text-xs uppercase px-5 h-11 flex items-center justify-center border border-amber1 text-amber1 hover:bg-amber1 hover:text-black">
              Browse workflows <ArrowRight size={14} className="ml-2" />
            </Link>
            <Link to="/publish?type=workflow" className="font-mono text-xs uppercase px-5 h-11 flex items-center justify-center border border-border1 hover:border-amber1">
              Publish a workflow
            </Link>
          </div>
        </div>
        {/* Phase 26b — theme-aware terminal demo. Re-uses .install-terminal
            tokens defined in index.css so dark theme stays cinematic and
            light theme renders white-on-ink instead of a black slab. */}
        <div className="rounded-lg border install-terminal overflow-hidden">
          <div className="install-terminal-bar flex items-center gap-2 px-4 py-2.5 border-b border-white/10">
            <div className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
            </div>
            <span className="ml-2 install-terminal-label font-mono text-[10.5px] uppercase tracking-[0.2em]">terminal · workflow demo</span>
          </div>
          <pre className="p-5 sm:p-6 m-0 font-mono text-[11px] sm:text-xs leading-relaxed overflow-x-auto whitespace-pre install-terminal-body">
<span className="tok-arg">$ horizon workflow run morning-brief</span>{"\n\n"}- collect local notes{"\n"}- check calendar plugin permission{"\n"}- summarize with selected model{"\n"}- open daily note{"\n"}- speak short brief{"\n\n"}<span className="tok-arg">done / 9.4s / controlled tools</span>
          </pre>
        </div>
      </div>
    </section>
  );
}

function CreatorsCTA() {
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-border1">
      <SectionHeader eyebrow="/developers" title="Build without pretending trust is automatic" />
      <div className="grid grid-cols-1 md:grid-cols-3 border border-border1">
        <CreatorCol icon={Sparkles} title="Build a plugin" body="Use the SDK to define tools, settings, permissions, and handler code. Keep the surface small enough to review." cta="SDK on GitHub" href="https://github.com/ErnestKostevich/horizon-plugin-sdk" external />
        <CreatorCol icon={Store} title="Submit for review" body="Upload through the web dashboard. Submissions land in moderation before they ever appear in the public catalog." cta="Publish now" to="/publish" borderLeft />
        <CreatorCol icon={KeyRound} title="Get paid safely" body="Wallet and payout availability are computed from backend state, not stale UI state. Authors keep 70% after approval and sale." cta="Monetization rules" to="/docs/monetization" borderLeft />
      </div>
    </section>
  );
}

function CreatorCol({ icon: Icon, title, body, cta, to, href, external, borderLeft }) {
  const inner = (
    <>
      <Icon size={22} strokeWidth={1.5} className="text-amber1 mb-3" />
      <div className="font-display text-base text-text1">{title}</div>
      <p className="font-body text-sm text-text2 mt-2 leading-relaxed">{body}</p>
      <div className="mt-5 font-mono text-xs uppercase text-amber1 flex items-center">
        {cta} <ArrowRight size={13} className="ml-2" />
      </div>
    </>
  );
  const cls = `p-6 bg-surface border-t md:border-t-0 ${borderLeft ? "md:border-l" : ""} border-border1 hover:bg-black transition-colors`;
  if (external) return <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>{inner}</a>;
  return <Link to={to} className={cls}>{inner}</Link>;
}

function FinalCTA() {
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-surface p-8 sm:p-12 md:p-16 final-cta-banner">
        {/* Phase 20 — animated mesh backdrop, same trick as Hero but
            scaled down. Gives the closing section a real "moment"
            instead of looking like another grey card. */}
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-brand-cyan/25 blur-3xl animate-blob" style={{ animationDuration: "16s" }} />
          <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-brand-violet/22 blur-3xl animate-blob" style={{ animationDuration: "20s", animationDelay: "-4s" }} />
          <div className="absolute -bottom-32 right-0 w-[460px] h-[460px] rounded-full bg-brand-amber/18 blur-3xl animate-blob" style={{ animationDuration: "18s", animationDelay: "-8s" }} />
        </div>
        <div className="absolute inset-0 grid-bg opacity-15 pointer-events-none" />

        <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-4">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping-slow absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
              </span>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-accent">/release-ready direction</div>
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight text-white leading-[1.1] max-w-2xl">
              A personal agent starts with{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-cyan via-brand-violet to-brand-amber bg-[length:200%_auto] animate-gradient-shift">
                trust
              </span>
              .
            </h2>
            <p className="font-body text-base text-text2 mt-4 max-w-xl leading-relaxed">
              Local-first by default. Your keys, your data, your hardware.
              Every shell command, file write, and message send goes through
              a permission gate you can see.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            <a
              href="#download"
              className="group relative font-display text-sm font-semibold px-7 h-12 flex items-center justify-center text-bg rounded-full overflow-hidden transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-amber1 via-amber2 to-amber1 bg-[length:200%_auto] group-hover:bg-[position:100%_center] transition-[background-position] duration-500" />
              <span className="absolute inset-0 shadow-[0_0_25px_rgba(244,201,93,0.55)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-full" />
              <span className="relative flex items-center">
                <Download size={16} className="mr-2" /> Get the app
              </span>
            </a>
            <a
              href="https://github.com/ErnestKostevich/horizon-genesis"
              target="_blank"
              rel="noopener noreferrer"
              className="font-display text-sm font-medium px-6 h-12 flex items-center justify-center border border-white/15 text-text1 rounded-full bg-white/5 hover:bg-white/10 hover:border-white/30 transition-all duration-300"
            >
              <Github size={16} className="mr-2" /> Source on GitHub
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionHeader({ eyebrow, title, right }) {
  return (
    <div className="flex items-end justify-between mb-8 gap-6">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-text2">{eyebrow}</div>
        <h2 className="font-display text-2xl md:text-3xl tracking-tight text-text1 mt-2">{title}</h2>
      </div>
      {right}
    </div>
  );
}
